import { useEffect, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/react/ui/carousel';
import { Button } from '@/components/react/ui/button';
import AutoPlay from 'embla-carousel-autoplay';
import EmblaClassNames from 'embla-carousel-class-names';
// import { PauseIcon } from '@/components/react/assets/icons/PauseIcon';
// import { PlayIcon } from '@/components/react/assets/icons/PlayIcon';
import { Chevron } from '@/components/react/assets/icons/Chevron';
import { cn } from '@/lib/utils';
import { TestimonialCard } from './TestimonialCard';
import type { Testimonial } from '@/schemas/components/molecules/testimonialSchema';

interface TestimonialCarouselProps {
  testimonials: Testimonial[];
}

export function TestimonialCarousel({
  testimonials,
}: TestimonialCarouselProps) {
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const [logosApi, setLogosApi] = useState<CarouselApi>();
  const [autoPlayPlugin] = useState(() => AutoPlay({ delay: 4000 }));

  // // function to toggle autoplay and set the state of isPlayingToggled
  // const handlePlayPause = () => {
  //   if (isPlaying) {
  //     autoPlayPlugin.stop();
  //   } else {
  //     autoPlayPlugin.play();
  //   }
  //   setIsPlaying(!isPlaying);
  //   setIsPlayingToggled(true);
  // };

  const scrollNext = () => {
    mainApi?.scrollNext();
  };

  const scrollPrev = () => {
    mainApi?.scrollPrev();
  };

  const handleThumbnailClick = (index: number) => {
    mainApi?.scrollTo(index);
    autoPlayPlugin.reset();
  };

  // Synchronize the carousels: when the bottom carousel changes, update the top scroll position.
  useEffect(() => {
    if (!mainApi || !logosApi) return;

    const onSelect = () => logosApi.scrollTo(mainApi.selectedScrollSnap());

    mainApi.on('select', onSelect);
    logosApi.scrollTo(mainApi.selectedScrollSnap());

    return () => {
      mainApi.off('select', onSelect);
    };
  }, [mainApi, logosApi, testimonials]);

  return (
    <>
      {/* Top Logos Carousel */}
      <Carousel
        opts={{
          loop: true,
          dragFree: false,
          watchDrag: false,
          inViewThreshold: 1.0,
        }}
        setApi={setLogosApi}
        plugins={[EmblaClassNames()]}
        className="relative max-w-full md:max-w-100"
      >
        <CarouselContent carouselContentClassName="overflow-visible">
          {testimonials.map(({ companyLogo, authorName }, index) => {
            return (
              <CarouselItem
                key={`logo_${index}`}
                className="mx-3 flex flex-none basis-1/2 items-center justify-center px-5 py-4.5 opacity-25 [.is-snapped]:opacity-100"
              >
                {/* Use input for accessibility purposes */}
                <input
                  type="image"
                  className="flex max-h-25 w-auto max-w-45 cursor-pointer object-cover transition-transform hover:scale-105"
                  aria-label={`View testimonial from ${authorName}`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleThumbnailClick(index);
                    }
                  }}
                  onClick={() => handleThumbnailClick(index)}
                  src={companyLogo.url}
                  alt={companyLogo?.alternativeText ?? ''}
                />
              </CarouselItem>
            );
          })}
        </CarouselContent>
        {/* <div className="absolute top-1/2 right-6 z-10 flex -translate-y-1/2 justify-center md:right-0">
          <Button
            onClick={() => handlePlayPause()}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            size="icon"
            variant="carouselNav"
          >
            {isPlaying ?
              <PauseIcon
                width={16}
                height={16}
                className="[&_path]:fill-brand-purple-black"
              />
            : <PlayIcon
                width={16}
                height={16}
                className="[&_path]:fill-brand-purple-black"
              />
            }
          </Button>
        </div> */}
      </Carousel>

      {/* Main Testimonial Carousel */}
      <Carousel
        opts={{
          loop: true,
        }}
        plugins={[autoPlayPlugin]}
        setApi={setMainApi}
        className="relative w-full"
      >
        <div className="grid grid-cols-12 items-center">
          <div className="col-span-1 hidden justify-center md:flex">
            <CarouselNav direction="Previous" handleClick={scrollPrev} />
          </div>
          <div className="col-span-12 md:col-span-10">
            <CarouselContent>
              {testimonials.map((item, index) => (
                <CarouselItem
                  key={`${item.authorName}_${index}`}
                  className="mx-auto md:mx-0"
                >
                  <TestimonialCard className="w-full" testimonial={item} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>
          <div className="col-span-1 hidden justify-center md:flex">
            <CarouselNav direction="Next" handleClick={scrollNext} />
          </div>
        </div>
      </Carousel>
      <div className="flex w-full flex-row items-center justify-center gap-4 md:hidden">
        <CarouselNav direction="Previous" handleClick={scrollPrev} />
        <CarouselNav direction="Next" handleClick={scrollNext} />
      </div>
    </>
  );
}

function CarouselNav({
  direction,
  handleClick,
}: {
  readonly direction: 'Next' | 'Previous';
  readonly handleClick: () => void;
}) {
  return (
    <Button
      aria-label={`${direction} slide`}
      className="relative"
      onClick={handleClick}
      size="icon"
      variant="carouselNav"
    >
      <Chevron
        width={7}
        height={12}
        className={cn(
          '[&_path]:fill-brand-purple-black absolute top-1/2 left-1/2 -translate-1/2',
          {
            'rotate-180': direction === 'Previous',
          },
        )}
      />
    </Button>
  );
}
